import React from "react";

/**
 * Shared "holo" stat card — matches the Dashboard's signature look:
 *   - conic-gradient hairline border (`holo-border`)
 *   - hover sheen (`scan-sheen`)
 *   - translucent slate-900/60 with backdrop blur
 *   - hover lift
 *   - mono uppercase eyebrow label + icon tile + font-display value + colored sub
 *
 * Props:
 *   label    – small uppercase eyebrow text
 *   value    – the big primary value (string or node)
 *   sub      – optional smaller sub-line (string or node)
 *   icon     – lucide-react icon component
 *   positive – true | false | undefined → emerald / rose / slate for the sub line
 *   tone     – override tone for the icon tile (`emerald` default | `rose` | `amber` | `sky` | `indigo`)
 *   testId   – data-testid on the root
 *   valueClassName – override classes on the big value (e.g. tighter color)
 */
const TONE_CLASSES = {
  emerald: "text-emerald-300/90",
  rose: "text-rose-300/90",
  amber: "text-amber-300/90",
  sky: "text-sky-300/90",
  indigo: "text-indigo-300/90",
};

export default function StatCard({
  label,
  value,
  sub,
  icon: Icon,
  positive,
  tone = "emerald",
  testId,
  valueClassName = "",
}) {
  const subColor =
    positive === true
      ? "text-emerald-400"
      : positive === false
      ? "text-rose-400"
      : "text-slate-500";

  return (
    <div
      data-testid={testId}
      className="holo-border scan-sheen bg-slate-900/60 backdrop-blur-sm rounded-xl p-4 md:p-5 transition-transform hover:-translate-y-0.5"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono truncate">
          {label}
        </div>
        {Icon && (
          <div className="shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-slate-800 to-slate-900 ring-1 ring-slate-700/70 flex items-center justify-center">
            <Icon size={14} className={TONE_CLASSES[tone] || TONE_CLASSES.emerald} />
          </div>
        )}
      </div>
      <div
        className={`mt-3 text-xl md:text-2xl font-display tracking-tight tabular-nums ${
          valueClassName || "text-white"
        }`}
      >
        {value}
      </div>
      {sub !== undefined && sub !== null && (
        <div className={`mt-1 text-xs font-mono tabular-nums ${subColor}`}>{sub}</div>
      )}
    </div>
  );
}
